import { useCallback, useEffect, useMemo, useState } from "react";
import { createAdmin, login, logout, requestPasswordReset, resetPassword, updateMyAccount, watchAuth } from "../../api/auth";
import {
  createProfile,
  emptyProfile,
  getProfile,
  isSetupComplete,
  updateProfile as saveProfile,
} from "../../api/profile";
import { LEAD_STATUSES, deleteLead, getLeads, setLeadStatus } from "../../api/leads";
import { emptySeo, getSeoSettings, updateSeoSettings } from "../../api/seo";
import {
  clientFromLead,
  createClient,
  deleteClient,
  getClients,
  updateClient,
} from "../../api/clients";
import { createOrder, deleteOrder, getOrders, updateOrder } from "../../api/orders";
import { createUser, deleteUser, getUsers, updateUser, USER_ROLES } from "../../api/users";
import { createInvite, deleteInvite, getInvites } from "../../api/invites";
import {
  clientFromProspect,
  createProspect,
  deleteProspect,
  getProspects,
  importProspectCsv,
  isFollowUpDue,
  patchProspect,
  updateProspect,
} from "../../api/prospects";
import { site } from "../../data/site";
import ClientsPanel from "./ClientsPanel";
import OrdersPanel from "./OrdersPanel";
import SalesPanel from "./SalesPanel";
import UsersPanel from "./UsersPanel";
import { Field, IconField, PanelMessage, PasswordField, Spinner, formatDate, friendlyError, toDate } from "./shared";
import {
  OverviewIcon,
  LeadsIcon,
  SalesIcon,
  ClientsIcon,
  OrdersIcon,
  CompanyIcon,
  SeoIcon,
  TeamIcon,
  BellIcon,
  CalendarIcon,
  CoinIcon,
  ChartIcon,
  ExternalLinkIcon,
  LinkIcon,
  AccountIcon,
  MailIcon,
  LogoutIcon,
} from "./icons";
import styles from "./Admin.module.css";

/*
  Visible Online control panel (/cpanel).

  This is the internal back office for Visible Online itself — the company that
  runs this website. It is NOT a client-facing tool and has nothing to do with
  the businesses we build websites or Google profiles for. From here the team:
    • reviews and follows up on enquiries sent through the contact form,
    • keeps Visible Online's own contact details up to date,
    • manages the site-wide SEO / social share tags.
*/

// Visible Online's details as shipped in src/data/site.js. Used to pre-fill
// the company form so the first save never starts from blank fields.
const companyDefaults = {
  name: site.name,
  tagline: site.tagline,
  phone: site.contact.phone,
  email: site.contact.email,
  whatsapp: site.contact.whatsapp,
  location: site.contact.location,
  hours: "",
  about: site.about,
};

const TABS = [
  { id: "overview", label: "Overview", icon: OverviewIcon, hint: "What needs attention today." },
  { id: "leads", label: "Enquiries", icon: LeadsIcon, hint: "Messages sent through the contact form." },
  { id: "sales", label: "Sales", icon: SalesIcon, hint: "Businesses the team is approaching." },
  { id: "clients", label: "Clients", icon: ClientsIcon, hint: "Everyone Visible Online has worked with." },
  { id: "orders", label: "Orders", icon: OrdersIcon, hint: "Plans, software and services sold, with payments." },
  { id: "company", label: "Company details", icon: CompanyIcon, adminOnly: true, hint: "Visible Online's own contact information." },
  { id: "seo", label: "SEO", icon: SeoIcon, adminOnly: true, hint: "Site-wide title, description and share card." },
  { id: "users", label: "Team", icon: TeamIcon, adminOnly: true, hint: "Who can sign in to this control panel." },
];

function initialsOf(text) {
  const parts = String(text || "?").split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] || "?") + (parts.length > 1 ? parts[1][0] : "")).toUpperCase();
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [setupDone, setSetupDone] = useState(null); // null = unknown
  const [booting, setBooting] = useState(true);
  const [welcome, setWelcome] = useState(false); // show the splash after a fresh sign-in

  useEffect(() => {
    let active = true;
    isSetupComplete()
      .then((done) => active && setSetupDone(done))
      .catch(() => active && setSetupDone(false));
    const unsub = watchAuth((u) => {
      if (!active) return;
      setUser(u);
      setBooting(false);
    });
    return () => {
      active = false;
      unsub();
    };
  }, []);

  if (booting || setupDone === null) {
    return (
      <Shell>
        <div className={styles.notice}>
          <Spinner />
        </div>
      </Shell>
    );
  }

  if (!setupDone) {
    return (
      <Shell>
        <SetupForm onDone={() => setSetupDone(true)} />
      </Shell>
    );
  }

  if (!user) {
    return (
      <Shell noBrand>
        <LoginForm onLoggedIn={() => setWelcome(true)} />
      </Shell>
    );
  }

  return (
    <Shell wide>
      {welcome && <WelcomeSplash user={user} onDone={() => setWelcome(false)} />}
      <Dashboard user={user} />
    </Shell>
  );
}

// Full-screen status shown after a sign-in attempt. It animates in, holds
// for a moment and fades out. `tone` is "success" (drawn tick) or "danger"
// (drawn cross).
function AuthSplash({ tone = "success", title, text, onDone, duration = 2600 }) {
  useEffect(() => {
    const t = setTimeout(onDone, duration);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`${styles.welcome} ${tone === "danger" ? styles.welcomeDanger : ""}`}
      style={{ animationDuration: `${duration}ms` }}
      role={tone === "danger" ? "alert" : "status"}
      aria-live={tone === "danger" ? "assertive" : "polite"}
    >
      <div className={styles.welcomeInner}>
        <span className={styles.welcomeCheck} aria-hidden="true">
          <svg viewBox="0 0 52 52" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <circle className={styles.welcomeRing} cx="26" cy="26" r="23" />
            {tone === "danger" ? (
              <>
                <path className={styles.welcomeTick} d="M18 18l16 16" />
                <path className={`${styles.welcomeTick} ${styles.welcomeTick2}`} d="M34 18L18 34" />
              </>
            ) : (
              <path className={styles.welcomeTick} d="M16 27l7 7 14-15" />
            )}
          </svg>
        </span>
        <h2>{title}</h2>
        {text && <p>{text}</p>}
      </div>
    </div>
  );
}

function WelcomeSplash({ user, onDone }) {
  const name = String(user.displayName || user.email || "").split(/[\s@]/)[0];
  return (
    <AuthSplash
      title={`Welcome back${name ? `, ${name}` : ""}`}
      text="Signed in to the Visible Online control panel"
      onDone={onDone}
    />
  );
}

// Auth screens show the brand name; the dashboard sidebar only shows the
// "cPanel" wordmark.
function Brand({ className }) {
  if (className) {
    return (
      <a className={className} href="/" aria-label="Visible Online home">
        <span className={styles.mark}>cPanel</span>
      </a>
    );
  }
  return (
    <a className={styles.brand} href="/" aria-label="Visible Online home">
      <span className={styles.brandName}>Visible Online</span>
      <span>Control panel</span>
    </a>
  );
}

// The dashboard puts the brand inside its sidebar, so the wide shell skips it.
// Screens that want no header at all (the login page) pass noBrand.
export function Shell({ children, wide, noBrand }) {
  return (
    <div className={styles.page}>
      <div className={`${styles.container} ${wide ? styles.wide : ""}`}>
        {!wide && !noBrand && <Brand />}
        {children}
      </div>
    </div>
  );
}

/* ---------------- First-run setup ---------------- */

function SetupForm({ onDone }) {
  const [form, setForm] = useState({
    displayName: "",
    email: "",
    password: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    try {
      const admin = await createAdmin({
        email: form.email.trim(),
        password: form.password,
        displayName: form.displayName.trim(),
      });
      // Seed Visible Online's company record from the values in site.js; the
      // team can refine them later from the "Company details" tab.
      await createProfile(admin.uid, companyDefaults);
      onDone();
    } catch (err) {
      setError(friendlyError(err));
      setBusy(false);
    }
  };

  return (
    <form className={styles.card} onSubmit={submit}>
      <span className={styles.badge}>First-time setup</span>
      <h1>Set up the control panel</h1>
      <p className={styles.sub}>
        One-time step. This creates the first admin login for managing
        visibleonline.in. You can add the rest of the team later from the Team tab.
      </p>

      <fieldset className={styles.group} disabled={busy}>
        <legend>Team login</legend>
        <Field label="Your name" value={form.displayName} onChange={set("displayName")} />
        <Field label="Login email" type="email" value={form.email} onChange={set("email")} required />
        <Field
          label="Password"
          type="password"
          value={form.password}
          onChange={set("password")}
          required
          hint="At least 6 characters"
        />
      </fieldset>

      {error && <p className={styles.error}>{error}</p>}
      <button className={styles.primaryBtn} type="submit" disabled={busy}>
        {busy ? "Creating…" : "Create login & open the panel"}
      </button>
    </form>
  );
}

/* ---------------- Login ---------------- */

const CREDENTIAL_ERRORS = new Set(["invalid_credentials", "auth/invalid-credential", "http_401"]);

function LoginForm({ onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(0); // > 0 while the "Invalid credentials" splash shows; counts attempts
  const [mode, setMode] = useState("login"); // login | forgot | reset

  if (mode !== "login") {
    return <ForgotPassword initialEmail={email} onBack={() => setMode("login")} />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login({ email: email.trim(), password });
      onLoggedIn?.();
      // watchAuth updates state and re-renders into the dashboard.
    } catch (err) {
      if (CREDENTIAL_ERRORS.has(err?.code)) {
        // Animated "Invalid credentials" overlay, then a fresh form: no inline
        // error, both fields cleared.
        setDenied((n) => n + 1);
        setEmail("");
        setPassword("");
      } else {
        setError(friendlyError(err));
      }
      setBusy(false);
    }
  };

  return (
    <>
      {denied > 0 && (
        <AuthSplash
          key={denied}
          tone="danger"
          title="Invalid credentials"
          text="The email or password is incorrect. Please try again."
          duration={2200}
          onDone={() => setDenied(0)}
        />
      )}
    <form className={`${styles.card} ${denied > 0 ? styles.shake : ""}`} onSubmit={submit}>
      <h1 className={styles.loginTitle}>
        <span className={styles.mark}>cPanel</span>
      </h1>
      <p className={`${styles.sub} ${styles.loginSub}`}>Login to the Visible Online Workspace</p>
      <fieldset className={styles.group} disabled={busy}>
        <IconField label="Email" icon={<MailIcon />} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
        <PasswordField label="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
      </fieldset>
      <button type="button" className={styles.linkBtn} onClick={() => setMode("forgot")}>
        Forgot password?
      </button>
      {error && <p className={styles.error}>{error}</p>}
      <button className={styles.primaryBtn} type="submit" disabled={busy}>
        {busy ? "Signing in…" : "Sign in"}
      </button>
    </form>
    </>
  );
}

// Two steps: ask for the email, then enter the emailed code + a new password.
function ForgotPassword({ initialEmail, onBack }) {
  const [step, setStep] = useState("email"); // email | code | done
  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const sendCode = async (e) => {
    e?.preventDefault();
    setError("");
    setBusy(true);
    try {
      await requestPasswordReset(email.trim());
      setStep("code");
    } catch (err) {
      setError(friendlyError(err));
    }
    setBusy(false);
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await resetPassword({ email: email.trim(), otp: otp.trim(), password });
      setStep("done");
    } catch (err) {
      setError(friendlyError(err));
    }
    setBusy(false);
  };

  if (step === "done") {
    return (
      <div className={styles.card}>
        <h1 className={styles.loginTitle}>Password updated</h1>
        <button className={styles.primaryBtn} type="button" onClick={onBack}>Back to sign in</button>
      </div>
    );
  }

  return (
    <form className={styles.card} onSubmit={step === "email" ? sendCode : submitReset}>
      <h1 className={styles.loginTitle}>Reset password</h1>
      {step === "code" && <p className={styles.sub}>Enter the 6-digit code sent to {email.trim()}.</p>}
      <fieldset className={styles.group} disabled={busy}>
        {step === "email" ? (
          <IconField label="Email" icon={<MailIcon />} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
        ) : (
          <>
            <Field label="Code" inputMode="numeric" maxLength={6} pattern="[0-9]{6}" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} autoComplete="one-time-code" required />
            <PasswordField label="New password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </>
        )}
      </fieldset>
      {error && <p className={styles.error}>{error}</p>}
      <button className={styles.primaryBtn} type="submit" disabled={busy}>
        {step === "email" ? (busy ? "Sending…" : "Send code") : busy ? "Saving…" : "Reset password"}
      </button>
      {step === "code" && (
        <button type="button" className={styles.linkBtn} onClick={sendCode} disabled={busy}>Resend code</button>
      )}
      <button type="button" className={styles.linkBtn} onClick={onBack}>Back to sign in</button>
    </form>
  );
}

/* ---------------- Dashboard ---------------- */

function Dashboard({ user }) {
  const [tab, setTab] = useState("overview");
  const leads = useLeads();
  const clients = useClients();
  const orders = useOrders();
  const isAdmin = user.role === "admin";
  const users = useUsers(isAdmin);
  const invites = useInvites(isAdmin);
  const prospects = useProspects();
  const tabs = TABS.filter((t) => !t.adminOnly || isAdmin);
  const [orderDraft, setOrderDraft] = useState(null);

  // "Save as client" from an enquiry → creates the client and jumps to Clients.
  const saveLeadAsClient = async (lead) => {
    await clients.add(clientFromLead(lead));
    if (statusOf(lead) === "new") leads.updateStatus(lead.id, "contacted");
    setTab("clients");
  };

  // "Make client" from a prospect → creates the client, marks the prospect won.
  const convertProspect = async (prospect) => {
    if (!window.confirm(`Add "${prospect.businessName || prospect.contactName}" to Clients and mark as won?`)) return;
    const clientId = await clients.add(clientFromProspect(prospect));
    await prospects.patch(prospect.id, { stage: "won", clientId }).catch(() => {});
    setTab("clients");
  };

  const followUpsDue = (prospects.items || []).filter((p) => isFollowUpDue(p)).length;

  // "New order" from a client row → opens the order form with that client set.
  const startOrderFor = (client) => {
    setOrderDraft({ clientId: client.id });
    setTab("orders");
  };

  const active = tabs.find((t) => t.id === tab) || tabs[0];
  const heading = tab === "account" ? "My account" : active.label;
  const hint = tab === "account" ? "Your name, password and sign-in details." : active.hint;
  const roleLabel = USER_ROLES.find((r) => r.value === user.role)?.label || user.role;

  return (
    <div className={styles.layout}>
      <aside className={styles.sidebar}>
        <Brand className={styles.sideBrand} />
        <nav className={styles.sideNav} aria-label="Control panel sections">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                aria-current={tab === t.id ? "page" : undefined}
                className={tab === t.id ? styles.navItemActive : styles.navItem}
                onClick={() => setTab(t.id)}
              >
                <span className={styles.navLabel}>
                  {Icon && <Icon />}
                  {t.label}
                </span>
                {t.id === "leads" && leads.newCount > 0 && (
                  <span className={styles.count}>{leads.newCount}</span>
                )}
                {t.id === "sales" && followUpsDue > 0 && (
                  <span className={styles.count} title="Follow-ups due">{followUpsDue}</span>
                )}
              </button>
            );
          })}
        </nav>
        <div className={styles.sideFoot}>
          <button
            aria-current={tab === "account" ? "page" : undefined}
            className={tab === "account" ? styles.navItemActive : styles.navItem}
            onClick={() => setTab("account")}
            title="My account"
          >
            <span className={styles.navLabel}>
              <span className={styles.navAvatar} aria-hidden="true">{initialsOf(user.displayName || user.email)}</span>
              <span className={styles.navUser}>
                <strong>{user.displayName || user.email}</strong>
                <small>{roleLabel}</small>
              </span>
            </span>
          </button>
        </div>
      </aside>

      <div className={styles.main}>
        <header className={styles.dashHead}>
          <span className={`${styles.mark} ${styles.headMark}`} aria-hidden="true">cPanel</span>
          <div className={styles.headText} key={tab}>
            <h1>{heading}</h1>
            {hint && <p>{hint}</p>}
          </div>
          <a className={styles.ghostBtn} href="/" target="_blank" rel="noreferrer">
            View site <ExternalLinkIcon />
          </a>
        </header>

      {tab === "overview" && (
        <OverviewPanel leads={leads} clients={clients} orders={orders} onOpenLeads={() => setTab("leads")} />
      )}
      {tab === "leads" && <LeadsPanel leads={leads} clients={clients} onSaveAsClient={saveLeadAsClient} />}
      {tab === "sales" && <SalesPanel prospects={prospects} onConvert={convertProspect} />}
      {tab === "clients" && <ClientsPanel clients={clients} orders={orders} onNewOrder={startOrderFor} />}
      {tab === "orders" && (
        <OrdersPanel
          orders={orders}
          clients={clients}
          draft={orderDraft}
          onDraftHandled={() => setOrderDraft(null)}
        />
      )}
      {tab === "company" && isAdmin && <CompanyPanel />}
      {tab === "seo" && isAdmin && <SeoPanel />}
      {tab === "users" && isAdmin && <UsersPanel users={users} invites={invites} me={user} />}
      {tab === "account" && <AccountPanel user={user} />}
      </div>

      {leads.newCount > 0 && (
        <button
          type="button"
          key={leads.newCount}
          className={styles.bellFab}
          onClick={() => setTab("leads")}
          title={`${leads.newCount} ${leads.newCount === 1 ? "enquiry" : "enquiries"} awaiting reply`}
          aria-label={`${leads.newCount} ${leads.newCount === 1 ? "enquiry" : "enquiries"} awaiting reply. Open enquiries.`}
        >
          <BellIcon />
          <span className={styles.bellCount}>{leads.newCount > 99 ? "99+" : leads.newCount}</span>
        </button>
      )}
    </div>
  );
}

/* ---------------- My account ---------------- */

function AccountPanel({ user }) {
  const roleLabel = USER_ROLES.find((r) => r.value === user.role)?.label || user.role;

  return (
    <div className={styles.dash}>
      <section className={styles.card}>
        <div className={styles.accountHead}>
          <span className={styles.accountAvatarLg}><AccountIcon /></span>
          <div>
            <h2>{user.displayName || "Unnamed"}</h2>
            <p className={styles.sub}>{user.email}</p>
          </div>
          <span className={`${styles.pill} ${styles[`role_${user.role}`] || ""}`}>{roleLabel}</span>
        </div>
      </section>

      <AccountForm user={user} />

      <section className={styles.card}>
        <h2>Account actions</h2>
        <p className={styles.sub}>View the public site, or sign out of the control panel.</p>
        <div className={styles.formActions}>
          <a className={styles.ghostBtn} href="/" target="_blank" rel="noreferrer">View site <ExternalLinkIcon /></a>
          <button className={styles.dangerBtn} onClick={() => logout()}><LogoutIcon /> Log out</button>
        </div>
      </section>
    </div>
  );
}

function AccountForm({ user }) {
  const [form, setForm] = useState({ name: user.displayName || "", currentPassword: "", newPassword: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const set = (k) => (e) => {
    setSaved(false);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.newPassword) {
      if (!form.currentPassword) return setError("Enter your current password to set a new one.");
      if (form.newPassword.length < 6) return setError("New password must be at least 6 characters.");
      if (form.newPassword !== form.confirm) return setError("The new passwords don't match.");
    }
    setBusy(true);
    try {
      await updateMyAccount(form);
      setForm((f) => ({ ...f, currentPassword: "", newPassword: "", confirm: "" }));
      setSaved(true);
    } catch (err) {
      setError(err?.message || "Could not save your details.");
    }
    setBusy(false);
  };

  return (
    <form className={styles.card} onSubmit={submit}>
      <h2>Your details</h2>
      <p className={styles.sub}>Your email and role are managed by an admin.</p>
      <fieldset className={`${styles.group} ${styles.twoCol}`} disabled={busy}>
        <Field label="Name" value={form.name} onChange={set("name")} />
        <Field label="Login email" type="email" value={user.email} onChange={() => {}} disabled />
        <Field label="Current password" type="password" value={form.currentPassword} onChange={set("currentPassword")} autoComplete="current-password" hint="Only needed to change your password." />
        <Field label="New password" type="password" value={form.newPassword} onChange={set("newPassword")} autoComplete="new-password" />
        <Field label="Confirm new password" type="password" value={form.confirm} onChange={set("confirm")} autoComplete="new-password" />
      </fieldset>
      {error && <p className={styles.error}>{error}</p>}
      {saved && <p className={styles.sub}>Saved.</p>}
      <div className={styles.formActions}>
        <button className={styles.primaryBtn} type="submit" disabled={busy}>{busy ? "Saving…" : "Save changes"}</button>
      </div>
    </form>
  );
}

/* ---------------- Shared collection hooks ---------------- */

// Generic "load a list, mutate optimistically, reload on failure" helper.
function useCollection(fetcher) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    setItems(null);
    fetcher()
      .then(setItems)
      .catch((err) => setError(friendlyError(err)));
  }, [fetcher]);

  useEffect(() => {
    load();
  }, [load]);

  // Run a write; on failure surface the error and re-sync from the server.
  const run = useCallback(
    async (fn) => {
      try {
        return await fn();
      } catch (err) {
        setError(friendlyError(err));
        load();
        throw err;
      }
    },
    [load]
  );

  return { items, setItems, error, load, run };
}

function useClients() {
  const col = useCollection(getClients);
  const { setItems, run } = col;

  const add = useCallback(
    (data) =>
      run(async () => {
        const id = await createClient(data);
        const now = new Date();
        setItems((list) => [{ id, ...data, createdAt: now, updatedAt: now }, ...(list || [])]);
        return id;
      }),
    [run, setItems]
  );

  const update = useCallback(
    (id, data) =>
      run(async () => {
        await updateClient(id, data);
        setItems((list) => (list || []).map((c) => (c.id === id ? { ...c, ...data } : c)));
      }),
    [run, setItems]
  );

  const remove = useCallback(
    (id) =>
      run(async () => {
        setItems((list) => (list || []).filter((c) => c.id !== id));
        await deleteClient(id);
      }).catch(() => {}),
    [run, setItems]
  );

  return { ...col, add, update, remove };
}

function useOrders() {
  const col = useCollection(getOrders);
  const { setItems, run } = col;

  const add = useCallback(
    (data, client) =>
      run(async () => {
        const saved = await createOrder(data, client);
        const now = new Date();
        setItems((list) => [{ ...saved, createdAt: now, updatedAt: now }, ...(list || [])]);
        return saved;
      }),
    [run, setItems]
  );

  const update = useCallback(
    (id, data, client) =>
      run(async () => {
        const saved = await updateOrder(id, data, client);
        setItems((list) => (list || []).map((o) => (o.id === id ? { ...o, ...saved } : o)));
      }),
    [run, setItems]
  );

  const setStatus = useCallback(
    (id, status) =>
      run(async () => {
        setItems((list) => (list || []).map((o) => (o.id === id ? { ...o, status } : o)));
        const current = (col.items || []).find((o) => o.id === id);
        if (current) await updateOrder(id, { ...current, status });
      }).catch(() => {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [run, setItems, col.items]
  );

  const remove = useCallback(
    (id) =>
      run(async () => {
        setItems((list) => (list || []).filter((o) => o.id !== id));
        await deleteOrder(id);
      }).catch(() => {}),
    [run, setItems]
  );

  return { ...col, add, update, setStatus, remove };
}

// Sales prospects (businesses the team is approaching).
function useProspects() {
  const col = useCollection(getProspects);
  const { setItems, run } = col;

  const importCsv = async (data) => {
    const result = await importProspectCsv(data);
    col.load();
    return result;
  };

  const add = useCallback(
    (data) =>
      run(async () => {
        const saved = await createProspect(data);
        setItems((list) => [saved, ...(list || [])]);
        return saved;
      }),
    [run, setItems]
  );

  const update = useCallback(
    (id, data) =>
      run(async () => {
        await updateProspect(id, data);
        setItems((list) => (list || []).map((p) => (p.id === id ? { ...p, ...data, nextFollowUp: data.nextFollowUp || null } : p)));
      }),
    [run, setItems]
  );

  // Partial change (e.g. stage, clientId) applied optimistically.
  const patch = useCallback(
    (id, fields) =>
      run(async () => {
        setItems((list) => (list || []).map((p) => (p.id === id ? { ...p, ...fields } : p)));
        await patchProspect(id, fields);
      }),
    [run, setItems]
  );

  const setStage = useCallback((id, stage) => patch(id, { stage }).catch(() => {}), [patch]);

  const remove = useCallback(
    (id) =>
      run(async () => {
        setItems((list) => (list || []).filter((p) => p.id !== id));
        await deleteProspect(id);
      }).catch(() => {}),
    [run, setItems]
  );

  return { ...col, add, update, patch, setStage, remove, importCsv };
}

// Team members. Only admins may list them, so non-admins get an empty list
// instead of a 403 error on every dashboard load.
const noUsers = async () => [];

// Never keep a typed password in local state once it has been sent.
function withoutPassword(data) {
  const copy = { ...data };
  delete copy.password;
  return copy;
}

function useUsers(enabled) {
  const col = useCollection(enabled ? getUsers : noUsers);
  const { setItems, run } = col;

  const add = useCallback(
    (data) =>
      run(async () => {
        const id = await createUser(data);
        const now = new Date();
        setItems((list) => [...(list || []), { id, ...withoutPassword(data), active: true, createdAt: now, updatedAt: now }]);
        return id;
      }),
    [run, setItems]
  );

  const update = useCallback(
    (id, data) =>
      run(async () => {
        await updateUser(id, data);
        setItems((list) => (list || []).map((u) => (u.id === id ? { ...u, ...withoutPassword(data) } : u)));
      }),
    [run, setItems]
  );

  const remove = useCallback(
    (id) =>
      run(async () => {
        await deleteUser(id);
        setItems((list) => (list || []).filter((u) => u.id !== id));
      }).catch(() => {}),
    [run, setItems]
  );

  return { ...col, add, update, remove };
}

// Pending "Team" invites. Same enabled-gate as useUsers: only admins may list them.
const noInvites = async () => [];

function useInvites(enabled) {
  const col = useCollection(enabled ? getInvites : noInvites);
  const { setItems, run } = col;

  // Re-inviting the same address (Resend) replaces its pending row, matching
  // what the server does when it gets a POST /invites for an email that
  // already has one pending.
  const add = useCallback(
    (data) =>
      run(async () => {
        const id = await createInvite(data);
        const now = new Date();
        setItems((list) => [
          { id, ...data, createdAt: now },
          ...(list || []).filter((i) => i.email !== data.email),
        ]);
        return id;
      }),
    [run, setItems]
  );

  const remove = useCallback(
    (id) =>
      run(async () => {
        await deleteInvite(id);
        setItems((list) => (list || []).filter((i) => i.id !== id));
      }).catch(() => {}),
    [run, setItems]
  );

  return { ...col, add, remove };
}

/* ---------------- Enquiries data (shared by Overview + Enquiries) ---------------- */

function useLeads() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    setItems(null);
    getLeads()
      .then(setItems)
      .catch((err) => setError(friendlyError(err)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Quiet background refresh (keeps the current list on screen) so the
  // floating bell notices new enquiries while the panel is open.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "hidden") return;
      getLeads().then(setItems).catch(() => {});
    };
    const id = setInterval(tick, 60 * 1000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);

  const updateStatus = useCallback(async (id, status) => {
    setItems((list) => (list || []).map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await setLeadStatus(id, status);
    } catch (err) {
      setError(friendlyError(err));
      load();
    }
  }, [load]);

  const remove = useCallback(async (id) => {
    setItems((list) => (list || []).filter((l) => l.id !== id));
    try {
      await deleteLead(id);
    } catch (err) {
      setError(friendlyError(err));
      load();
    }
  }, [load]);

  const stats = useMemo(() => {
    const list = items || [];
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return {
      total: list.length,
      newCount: list.filter((l) => statusOf(l) === "new").length,
      thisWeek: list.filter((l) => toDate(l.createdAt)?.getTime() > weekAgo).length,
      closed: list.filter((l) => statusOf(l) === "closed").length,
    };
  }, [items]);

  return { items, error, load, updateStatus, remove, ...stats };
}

const statusOf = (lead) => lead.status || "new";

/* ---------------- Overview ---------------- */

function OverviewPanel({ leads, clients, orders, onOpenLeads }) {
  const recent = (leads.items || []).slice(0, 5);
  const orderList = orders.items || [];
  const active = orderList.filter((o) => !["delivered", "cancelled"].includes(o.status));
  const outstanding = orderList
    .filter((o) => o.status !== "cancelled")
    .reduce((sum, o) => sum + (Number(o.due) || 0), 0);

  return (
    <div className={styles.dash}>
      <div className={styles.statGrid}>
        <Stat icon={BellIcon} label="Enquiries awaiting reply" value={leads.items ? leads.newCount : "…"} accent />
        <Stat icon={CalendarIcon} label="Enquiries, last 7 days" value={leads.items ? leads.thisWeek : "…"} />
        <Stat icon={ClientsIcon} label="Clients" value={clients.items ? clients.items.length : "…"} tone="teal" />
        <Stat icon={OrdersIcon} label="Open orders" value={orders.items ? active.length : "…"} tone="violet" />
        <Stat icon={CoinIcon} label="Balance to collect" value={orders.items ? "₹" + Math.round(outstanding).toLocaleString("en-IN") : "…"} tone="amber" />
        <Stat icon={ChartIcon} label="Total enquiries" value={leads.items ? leads.total : "…"} tone="green" />
      </div>

      <section className={styles.card}>
        <div className={styles.cardHead}>
          <div>
            <h2><LeadsIcon /> Latest enquiries</h2>
            <p className={styles.sub}>The five most recent messages from the contact form.</p>
          </div>
          <button className={styles.ghostBtn} onClick={onOpenLeads}>View all</button>
        </div>
        {leads.error && <p className={styles.error}>{leads.error}</p>}
        {!leads.items && !leads.error && <PanelMessage><Spinner /></PanelMessage>}
        {leads.items && recent.length === 0 && <PanelMessage>No enquiries yet.</PanelMessage>}
        {recent.length > 0 && (
          <ul className={styles.recentList}>
            {recent.map((l) => (
              <li key={l.id} className={styles.recentItem}>
                <div className={styles.recentMeta}>
                  <strong>{l.name || "Unnamed"}{l.businessName ? ` · ${l.businessName}` : ""}</strong>
                  <span>{formatDate(l.createdAt)}{l.service ? ` · ${l.service}` : ""}</span>
                  {l.message && <p>{l.message}</p>}
                </div>
                <StatusPill status={statusOf(l)} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={styles.card}>
        <h2><LinkIcon /> Quick links</h2>
        <p className={styles.sub}>Pages the team edits most often, opened in a new tab.</p>
        <div className={styles.quickLinks}>
          <a href="/pricing" target="_blank" rel="noreferrer">Pricing page <ExternalLinkIcon /></a>
          <a href="/about" target="_blank" rel="noreferrer">About page <ExternalLinkIcon /></a>
          <a href="/contact" target="_blank" rel="noreferrer">Contact form <ExternalLinkIcon /></a>
          <a href="/products" target="_blank" rel="noreferrer">Products <ExternalLinkIcon /></a>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, tone }) {
  const toneClass = tone ? styles[`tone_${tone}`] || "" : "";
  return (
    <div className={`${styles.stat} ${accent ? styles.statAccent : ""} ${toneClass}`}>
      {Icon && (
        <span className={styles.statIcon}>
          <Icon />
        </span>
      )}
      <div className={styles.statBody}>
        <strong>{value}</strong>
        <span>{label}</span>
      </div>
    </div>
  );
}

/* ---------------- Enquiries ---------------- */

function LeadsPanel({ leads, clients, onSaveAsClient }) {
  if (leads.error && !leads.items) {
    return (
      <PanelMessage>
        {leads.error} <button className={styles.linkBtn} onClick={leads.load}>Retry</button>
      </PanelMessage>
    );
  }
  if (leads.items === null) return <PanelMessage><Spinner /></PanelMessage>;

  const visible = leads.items;
  const savedLeadIds = new Set((clients.items || []).map((c) => c.leadId).filter(Boolean));

  return (
    <div className={styles.dash}>
      <div className={styles.toolbar}>
        <div className={styles.headActions}>
          <button className={styles.ghostBtn} onClick={leads.load}>Refresh</button>
        </div>
      </div>

      {leads.error && <p className={styles.error}>{leads.error}</p>}

      {visible.length === 0 ? (
        <PanelMessage>No enquiries yet.</PanelMessage>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Name</th>
                <th>Business</th>
                <th>Contact</th>
                <th>Service</th>
                <th>Message</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((l) => (
                <tr key={l.id} className={statusOf(l) === "closed" ? styles.rowClosed : ""}>
                  <td>{formatDate(l.createdAt)}</td>
                  <td>{l.name}</td>
                  <td>{l.businessName}</td>
                  <td className={styles.contactCell}>
                    {l.phone && (
                      <>
                        <a href={`tel:${l.phone.replace(/\s+/g, "")}`}>{l.phone}</a>
                        <a
                          href={`https://wa.me/${l.phone.replace(/\D/g, "")}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          WhatsApp
                        </a>
                      </>
                    )}
                    {l.email && <a href={`mailto:${l.email}`}>{l.email}</a>}
                  </td>
                  <td>{l.service}</td>
                  <td className={styles.msgCell}>{l.message}</td>
                  <td>
                    <select
                      className={`${styles.select} ${styles[`status_${statusOf(l)}`]}`}
                      value={statusOf(l)}
                      onChange={(e) => leads.updateStatus(l.id, e.target.value)}
                      aria-label="Enquiry status"
                    >
                      {LEAD_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      {savedLeadIds.has(l.id) ? (
                        <span className={`${styles.pill} ${styles.status_contacted}`}>Client saved</span>
                      ) : (
                        <button className={styles.solidBtn} onClick={() => onSaveAsClient(l).catch(() => {})}>
                          Save as client
                        </button>
                      )}
                      <button
                        className={styles.dangerBtn}
                        onClick={() => {
                          if (window.confirm("Delete this enquiry permanently?")) leads.remove(l.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const label = LEAD_STATUSES.find((s) => s.value === status)?.label || status;
  return <span className={`${styles.pill} ${styles[`status_${status}`]}`}>{label}</span>;
}

/* ---------------- Company details (Visible Online itself) ---------------- */

function CompanyPanel() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getProfile()
      .then((p) => setForm({ ...emptyProfile, ...companyDefaults, ...(p || {}) }))
      .catch((err) => setError(friendlyError(err)));
  }, []);

  const set = (k) => (e) => {
    setSaved(false);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      // Only persist editable fields, never adminUid/timestamps.
      const { name, tagline, phone, email, whatsapp, location, hours, about } = form;
      await saveProfile({ name, tagline, phone, email, whatsapp, location, hours, about });
      setSaved(true);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  if (error && !form) return <PanelMessage>{error}</PanelMessage>;
  if (!form) return <PanelMessage><Spinner /></PanelMessage>;

  return (
    <form className={styles.card} onSubmit={submit}>
      <h2><CompanyIcon /> Contact information</h2>
      <p className={styles.sub}>
        Our own contact information as we want customers to see it. The public
        pages currently read these values from the code (src/data/site.js), so
        after changing a phone number or email here, update it there as well.
      </p>
      <fieldset className={`${styles.group} ${styles.twoCol}`} disabled={busy}>
        <Field label="Company name" value={form.name} onChange={set("name")} />
        <Field label="Tagline" value={form.tagline} onChange={set("tagline")} />
        <Field label="Phone" value={form.phone} onChange={set("phone")} />
        <Field label="Email" type="email" value={form.email} onChange={set("email")} />
        <Field label="WhatsApp" value={form.whatsapp} onChange={set("whatsapp")} />
        <Field label="Service area" value={form.location} onChange={set("location")} />
        <Field label="Working hours" value={form.hours} onChange={set("hours")} hint="e.g. Mon–Sat, 10am–7pm" />
      </fieldset>
      <fieldset className={styles.group} disabled={busy}>
        <Field label="About Visible Online" value={form.about} onChange={set("about")} textarea />
      </fieldset>
      {error && <p className={styles.error}>{error}</p>}
      {saved && <p className={styles.success}>Company details saved.</p>}
      <div className={styles.formActions}>
        <button className={styles.primaryBtn} type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
}

/* ---------------- SEO / meta tags ---------------- */

function SeoPanel() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getSeoSettings()
      .then((s) => setForm({ ...emptySeo, ...(s || {}) }))
      .catch((err) => setError(friendlyError(err)));
  }, []);

  const set = (k) => (e) => {
    setSaved(false);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { siteName, title, description, keywords, image } = form;
      await updateSeoSettings({ siteName, title, description, keywords, image });
      setSaved(true);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  if (error && !form) return <PanelMessage>{error}</PanelMessage>;
  if (!form) return <PanelMessage><Spinner /></PanelMessage>;

  return (
    <form className={styles.card} onSubmit={submit}>
      <h2><SeoIcon /> Meta tags &amp; social share card</h2>
      <p className={styles.sub}>
        Site-wide defaults for visibleonline.in: the homepage title, meta
        description and social share card. Pages with their own title (Pricing,
        About, …) keep it. Changes take effect on the next page load.
      </p>
      <fieldset className={styles.group} disabled={busy}>
        <Field
          label="Site name"
          value={form.siteName}
          onChange={set("siteName")}
          hint="Brand name used in social share cards (og:site_name)."
        />
        <Field
          label="Homepage title"
          value={form.title}
          onChange={set("title")}
          hint="Shown in the browser tab and search results. Aim for 50–60 characters."
        />
        <Field
          label="Meta description"
          value={form.description}
          onChange={set("description")}
          textarea
          hint="Summary under the title in search results. Aim for 150–160 characters."
        />
        <Field
          label="Keywords"
          value={form.keywords}
          onChange={set("keywords")}
          hint="Comma-separated. Optional — most search engines ignore these."
        />
        <Field
          label="Social share image URL"
          value={form.image}
          onChange={set("image")}
          hint="Absolute URL used for og:image / Twitter cards. Optional."
        />
      </fieldset>
      {error && <p className={styles.error}>{error}</p>}
      {saved && <p className={styles.success}>SEO settings saved.</p>}
      <div className={styles.formActions}>
        <button className={styles.primaryBtn} type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
}
